/**
 * HomeHero4Step — A/B test variant of the home hero exposed at `/home`.
 *
 * Behavioural shift vs. the default hero at `/`: instead of showing plan
 * pricing upfront, we walk the visitor through a 4-step audit journey
 * before asking them to pay. The idea is that unlocking an already-
 * generated result feels less like buying an uncertain service.
 *
 *   Step 1 — Upload profile PDF
 *   Step 2 — Pick a goal
 *   Step 3 — Deterministic audit preview  (reuses /api/free-score/from-pdf)
 *   Step 4 — Plan picker with the same INR/USD Razorpay flow used elsewhere
 *
 * Not linked from the site nav. Direct-URL only, seeded via ad
 * traffic. All progress lives in local component state; nothing new
 * is persisted server-side until the user hits Step 4 and clicks Pay.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Upload, Rocket, Lock, ArrowRight, ArrowLeft, Loader2,
  CheckCircle2, TrendingUp, ShieldCheck, Zap, Award, Users, Eye,
  FileText, Briefcase, User as UserIcon, Star, MessageCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { detectCurrency, cachedGeoCountry, fetchServerCurrency } from '../../lib/billing';
import RazorpayPaymentButton from '../billing/RazorpayPaymentButton';
import { GuestCheckoutDialog } from '../billing/GuestCheckoutDialog';
import { trackLead } from '../../lib/analytics';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const BUTTON_IDS = {
  one_time: { USD: 'pl_TJnRyxONxvizbT' },
  expert_audit: { INR: 'pl_TOXIkpjn3RbClI', USD: 'pl_TOXMNXZX049VAi' },
};

const GOALS = [
  {
    key: 'recruiter_visibility',
    icon: Briefcase,
    label: 'Get noticed by recruiters',
    body: 'Improve search visibility and communicate your professional value.',
  },
  {
    key: 'thought_leadership',
    icon: Rocket,
    label: 'Build thought leadership',
    body: 'Establish authority and make your expertise more memorable.',
  },
  {
    key: 'business_development',
    icon: TrendingUp,
    label: 'Win clients and business opportunities',
    body: 'Position yourself clearly for prospects, partners and decision-makers.',
  },
];

function ScoreRingLarge({ score = 72 }) {
  const c = 2 * Math.PI * 44;
  const clamped = Math.max(0, Math.min(100, Number(score) || 0));
  const off = c - (c * clamped) / 100;
  const tone = clamped >= 75 ? 'text-emerald-500' : clamped >= 55 ? 'text-sky-500' : 'text-amber-500';
  return (
    <div className="relative w-[132px] h-[132px]" data-testid="hero4-score-ring">
      <svg width="132" height="132" viewBox="0 0 100 100" className="-rotate-90">
        <circle cx="50" cy="50" r="44" fill="none" stroke="rgba(148,163,184,0.25)" strokeWidth="8" />
        <circle
          cx="50" cy="50" r="44" fill="none" stroke="currentColor" strokeWidth="8"
          strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round"
          className={tone}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className={`text-[38px] font-bold tabular-nums ${tone}`}>{Math.round(clamped)}</div>
        <div className="text-[9.5px] font-mono-edge tracking-[0.16em] uppercase text-slate-500">Profile score</div>
      </div>
    </div>
  );
}

export function HomeHero4Step() {
  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [goal, setGoal] = useState('recruiter_visibility');
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null); // free-score response
  const [previewErr, setPreviewErr] = useState(null);
  const [currency, setCurrency] = useState(() => detectCurrency());
  const [country, setCountry] = useState(() => cachedGeoCountry());
  const [payPlan, setPayPlan] = useState(null); // 'one_time' | 'expert_audit'
  const [guestCheckoutOpen, setGuestCheckoutOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    fetchServerCurrency().then((geo) => {
      if (!alive || !geo) return;
      setCountry(geo.country || '');
      if (geo.currency) setCurrency(geo.currency);
      // NB: never write to CURRENCY_KEY on auto-detect — that key is
      // reserved for explicit user overrides, otherwise a VPN change
      // won't take effect on the next visit (localStorage would still
      // return the first-visit currency).
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  // ------------- Step 1 handlers ----------------
  const onFilePicked = (f) => {
    if (!f) return;
    if (!f.name?.toLowerCase().endsWith('.pdf')) {
      toast.error('Please upload a LinkedIn PDF (File → Save to PDF from LinkedIn).');
      return;
    }
    setFile(f);
  };

  const startAudit = async () => {
    if (!file) {
      toast.error('Please upload your LinkedIn profile PDF first.');
      return;
    }
    // Move to step 2 (goal picker). We only hit the LLM after they pick.
    setStep(1);
  };

  // ------------- Step 2 handler ----------------
  const runPreview = useCallback(async () => {
    if (!file) {
      toast.error('Something went wrong — please re-upload your PDF.');
      setStep(0);
      return;
    }
    setUploading(true);
    setPreviewErr(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('status', 'professional');
      fd.append('objective', goal);
      const { data } = await axios.post(
        `${BACKEND}/api/free-score/from-pdf`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 }
      );
      setPreview(data);
      setStep(2);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'object'
        ? (detail?.message || 'Could not analyse the PDF. Please try another one.')
        : (detail || 'Could not analyse the PDF. Please try another one.');
      setPreviewErr(msg);
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  }, [file, goal]);

  // ------------- Step 4 handler ----------------
  // Clicking a plan CTA opens the payment surface directly — no
  // `/pricing` redirect. INR ₹199 opens the Razorpay JS Checkout via
  // `GuestCheckoutDialog` (single click → modal). All other (plan,
  // currency) pairs reveal an inline pay panel with Razorpay's hosted
  // payment-button widget mounted, ready to click. We deliberately keep
  // the hosted button in a permanent (once-mounted) panel so
  // payment-button.js only initialises once per session — this is what
  // prevents Razorpay's "Too many requests" 429s we hit when it lived
  // inside a remounting Dialog.
  const chooseMakeover = (planKey) => {
    setPayPlan(planKey);
    // Fire lead capture so admin sees this visitor even if they bail.
    try {
      axios.post(`${BACKEND}/api/hero-lead`, {
        linkedin_url: 'from-hero-v2-4step',
        goal,
        plan_key: planKey,
        currency,
      }).catch(() => {});
    } catch { /* ignore */ }
    // GA4 lead event — mirrors the /api/hero-lead capture above.
    trackLead('homepage_hero_4step', { plan: planKey, goal, currency });

    // INR ₹199 → Razorpay SDK modal via the guest-checkout dialog.
    if (planKey === 'one_time' && currency === 'INR') {
      setGuestCheckoutOpen(true);
      return;
    }

    // Others → scroll the pay panel into view so the Razorpay button
    // widget below the plan grid is instantly obvious.
    setTimeout(() => {
      const panel = document.querySelector('[data-testid="hero4-pay-panel"]');
      if (panel && panel.scrollIntoView) {
        panel.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 60);
  };

  const scoreVal = preview?.score_100 ?? preview?.score ?? 0;
  const strengths = preview?.strengths || [];
  const gaps = preview?.gaps || [];
  const limitingFactor = preview?.limiting_factor || '';
  const headlineText = preview?.extracted_evidence?.headline_text || '';
  const suggestedHeadline = preview?.headline_preview || '';

  return (
    <section
      className="relative overflow-x-clip isolate bg-[#050b1a] text-slate-100"
      aria-label="Get your LinkedIn Profile Makeover"
      id="home-hero-v2"
      data-testid="home-hero-v2"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(1100px circle at 78% 40%, hsl(190 95% 55% / 0.12), transparent 60%), radial-gradient(700px circle at 8% 8%, hsl(212 92% 55% / 0.09), transparent 60%)',
        }}
      />
      <div className="relative z-10 mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-10 py-10 sm:py-12">
        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] items-start">
          {/* LEFT — persistent pitch */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="lg:sticky lg:top-24 lg:self-start"
          >
            <p className="font-mono-edge text-[11px] tracking-[0.24em] uppercase text-yellow-300">
              LinkedIn Profile Optimization
            </p>
            <h1 className="mt-3 font-display font-semibold tracking-[-0.02em] leading-[1.05] text-white text-[32px] sm:text-[38px] lg:text-[42px]">
              Turn your LinkedIn profile into more{' '}
              <span className="text-emerald-400">interviews</span>,{' '}
              <span className="text-rose-400">clients</span> and{' '}
              <span className="text-sky-400">influence</span>.
            </h1>
            <p className="mt-3.5 text-[14.5px] text-slate-300/90 leading-relaxed max-w-[520px]">
              Discover exactly what&apos;s helping — or hurting — your profile in under 60 seconds. Then decide how you want to make it better.
            </p>
            <div className="mt-5 grid grid-cols-2 gap-2.5 max-w-[440px]">
              {[
                { icon: Briefcase, label: 'Corporate Professionals', color: 'sky' },
                { icon: UserIcon, label: 'Job Seekers', color: 'emerald' },
                { icon: TrendingUp, label: 'Consultants', color: 'blue' },
                { icon: Star, label: 'Coaches', color: 'rose' },
              ].map(({ icon: Icon, label, color }) => (
                <div
                  key={label}
                  className={`rounded-2xl border border-${color}-400/30 bg-${color}-500/[0.06] px-3 py-2.5 flex items-center gap-2`}
                >
                  <Icon size={16} className={`text-${color}-300`} strokeWidth={2.1} />
                  <p className="text-[12.5px] font-semibold text-slate-100 leading-tight">{label}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 flex flex-wrap gap-1.5 max-w-[520px]">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-sky-400/30 bg-sky-500/[0.08] px-2.5 py-1 text-[11.5px] text-slate-100">
                <Eye size={12} className="text-sky-300" /> Better visibility
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-blue-400/30 bg-blue-500/[0.08] px-2.5 py-1 text-[11.5px] text-slate-100">
                <ShieldCheck size={12} className="text-blue-300" /> Credible positioning
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-yellow-400/40 bg-yellow-500/[0.10] px-2.5 py-1 text-[11.5px] text-yellow-100">
                <MessageCircle size={12} className="text-yellow-300" /> More qualified conversations
              </span>
            </div>
          </motion.div>

          {/* RIGHT — stepped card */}
          <motion.div
            key={`step-${step}`}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="rounded-[22px] border border-white/10 bg-white p-5 sm:p-6 text-slate-900 shadow-2xl shadow-sky-900/40 min-h-[420px]"
            data-testid={`hero4-card-step-${step + 1}`}
          >
            {step === 0 && (
              <div data-testid="hero4-step1">
                <p className="text-[11px] font-mono-edge tracking-[0.2em] uppercase text-sky-600">Step 1 of 4 · Upload your profile</p>
                <h2 className="mt-1 text-[22px] font-bold text-slate-900 leading-tight">Get your LinkedIn Profile Makeover.</h2>
                <p className="mt-1.5 text-[13.5px] text-slate-600 leading-relaxed">
                  Upload your LinkedIn profile PDF and discover what&apos;s helping — or hurting — your visibility.
                </p>

                <label
                  htmlFor="hero4-file-input"
                  className="mt-4 flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-sky-300 bg-sky-50/50 hover:bg-sky-50 hover:border-sky-500 transition p-6 cursor-pointer text-center"
                  data-testid="hero4-file-dropzone"
                >
                  <Upload size={22} className="text-sky-500 mb-2" />
                  <p className="text-[13.5px] font-semibold text-slate-800">Drag and drop your PDF here or browse files</p>
                  <p className="mt-1 text-[11.5px] text-slate-500">PDF only · Secure upload · Your profile stays private</p>
                  {file && (
                    <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-100 text-emerald-800 text-[11.5px] px-2 py-0.5" data-testid="hero4-file-selected">
                      <CheckCircle2 size={11} /> {file.name}
                    </p>
                  )}
                  <input
                    id="hero4-file-input"
                    type="file"
                    accept="application/pdf"
                    className="hidden"
                    onChange={(e) => onFilePicked(e.target.files?.[0])}
                    data-testid="hero4-file-input"
                  />
                </label>

                <ol className="mt-4 space-y-1.5 text-[12.5px] text-slate-700 leading-snug">
                  <li>1. Open your LinkedIn profile on a desktop.</li>
                  <li>2. Click <b>Resources</b> or <b>More</b> below your introduction.</li>
                  <li>3. Select <b>Save to PDF</b>.</li>
                  <li>4. Upload the downloaded file here.</li>
                </ol>

                <button
                  type="button"
                  onClick={startAudit}
                  disabled={!file}
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-yellow-400 hover:bg-yellow-300 disabled:opacity-60 disabled:cursor-not-allowed text-slate-900 text-[14.5px] font-bold h-11 transition-colors"
                  data-testid="hero4-cta-step1"
                >
                  Upload &amp; Start My Audit <ArrowRight size={16} />
                </button>
              </div>
            )}

            {step === 1 && (
              <div data-testid="hero4-step2">
                <p className="text-[11px] font-mono-edge tracking-[0.2em] uppercase text-sky-600">Step 2 of 4 · Choose your goal</p>
                <h2 className="mt-1 text-[22px] font-bold text-slate-900 leading-tight">What do you want LinkedIn to help you achieve?</h2>
                <p className="mt-1.5 text-[13.5px] text-slate-600 leading-relaxed">
                  We&apos;ll evaluate and rewrite your profile based on your primary goal.
                </p>
                <div className="mt-4 space-y-2.5" role="radiogroup">
                  {GOALS.map((g) => {
                    const active = goal === g.key;
                    const Icon = g.icon;
                    return (
                      <button
                        key={g.key}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        onClick={() => setGoal(g.key)}
                        data-testid={`hero4-goal-${g.key}`}
                        className={`w-full text-left rounded-2xl border-2 px-4 py-3 transition-all flex items-start gap-3 ${
                          active
                            ? 'border-sky-500 bg-sky-50/70 shadow-[0_10px_30px_-15px_rgba(56,189,248,0.5)]'
                            : 'border-slate-200 bg-white hover:border-slate-300'
                        }`}
                      >
                        <span className={`mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-xl ${active ? 'bg-sky-500 text-white' : 'bg-sky-100 text-sky-700'}`}>
                          <Icon size={16} />
                        </span>
                        <span className="min-w-0">
                          <span className="block text-[14px] font-semibold text-slate-900 leading-tight">{g.label}</span>
                          <span className="block text-[12.5px] text-slate-600 mt-0.5 leading-snug">{g.body}</span>
                        </span>
                        {active && <CheckCircle2 size={16} className="text-sky-500 ml-auto shrink-0 mt-1" />}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-5 flex gap-2">
                  <button
                    type="button"
                    onClick={() => setStep(0)}
                    className="inline-flex items-center gap-1.5 rounded-2xl border border-slate-200 bg-white text-slate-700 text-[13px] font-semibold h-11 px-4"
                    data-testid="hero4-back-step2"
                  >
                    <ArrowLeft size={15} /> Back
                  </button>
                  <button
                    type="button"
                    onClick={runPreview}
                    disabled={uploading}
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-2xl bg-yellow-400 hover:bg-yellow-300 disabled:opacity-60 text-slate-900 text-[14.5px] font-bold h-11 transition-colors"
                    data-testid="hero4-cta-step2"
                  >
                    {uploading ? <Loader2 size={16} className="animate-spin" /> : null}
                    {uploading ? 'Analysing your profile…' : 'Analyse My Profile'}
                    {!uploading && <ArrowRight size={16} />}
                  </button>
                </div>
                {previewErr && (
                  <p className="mt-3 text-[12px] text-rose-600" data-testid="hero4-preview-error">{previewErr}</p>
                )}
              </div>
            )}

            {step === 2 && preview && (
              <div data-testid="hero4-step3">
                <p className="text-[11px] font-mono-edge tracking-[0.2em] uppercase text-sky-600">Step 3 of 4 · Audit preview</p>
                <h2 className="mt-1 text-[22px] font-bold text-slate-900 leading-tight">Your LinkedIn Profile Audit is ready.</h2>

                <div className="mt-4 rounded-2xl border border-sky-200 bg-sky-50/60 p-4 flex items-center gap-4">
                  <ScoreRingLarge score={scoreVal} />
                  <div className="min-w-0">
                    <p className="text-[10.5px] font-mono-edge tracking-[0.18em] uppercase text-sky-700">Overall</p>
                    <p className="mt-1 text-[14px] font-semibold text-slate-900 leading-snug">
                      {scoreVal >= 75
                        ? 'Strong foundation. Sharpen your headline and keywords for even better visibility.'
                        : scoreVal >= 55
                          ? 'Good foundation, but your positioning and keywords need improvement.'
                          : 'There is significant room to lift how recruiters and buyers see you.'}
                    </p>
                  </div>
                </div>

                {suggestedHeadline && (
                  <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
                    <p className="text-[10.5px] font-mono-edge tracking-[0.18em] uppercase text-slate-500">Headline analysis</p>
                    {headlineText && (
                      <>
                        <p className="mt-2 text-[11.5px] text-slate-500">Your current headline</p>
                        <p className="text-[13px] text-slate-700 italic leading-snug">&ldquo;{headlineText}&rdquo;</p>
                      </>
                    )}
                    <p className="mt-3 text-[11.5px] text-emerald-700">Recommended headline</p>
                    <p className="text-[13px] font-semibold text-slate-900 leading-snug">&ldquo;{suggestedHeadline}&rdquo;</p>
                  </div>
                )}

                {(strengths.length > 0 || gaps.length > 0) && (
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {strengths.length > 0 && (
                      <div className="rounded-2xl border border-emerald-200 bg-emerald-50/50 p-3.5">
                        <p className="text-[10.5px] font-mono-edge tracking-[0.18em] uppercase text-emerald-700">What&apos;s working</p>
                        <ul className="mt-2 space-y-1.5">
                          {strengths.slice(0, 3).map((s, i) => (
                            <li key={i} className="flex items-start gap-1.5 text-[12.5px] text-slate-700 leading-snug">
                              <CheckCircle2 size={12} className="mt-0.5 shrink-0 text-emerald-500" /> {s}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <div className="rounded-2xl border border-amber-200 bg-amber-50/50 p-3.5">
                      <p className="text-[10.5px] font-mono-edge tracking-[0.18em] uppercase text-amber-700">What&apos;s limiting your profile</p>
                      <p className="mt-2 text-[12.5px] text-slate-700 leading-snug">
                        {limitingFactor || (gaps.length > 0 ? gaps.slice(0, 3).join(' ') : 'Your positioning language could be sharper to match what recruiters search for.')}
                      </p>
                    </div>
                  </div>
                )}

                {/* Blurred locked previews */}
                <div className="mt-4 rounded-2xl border border-slate-200 bg-white overflow-hidden">
                  <div className="p-4 border-b border-slate-100">
                    <p className="text-[10.5px] font-mono-edge tracking-[0.18em] uppercase text-slate-500">Preview · About rewrite</p>
                    <p className="mt-2 text-[13px] font-semibold text-slate-900 leading-snug">
                      I help B2B technology brands turn complex offerings into compelling growth stories…
                    </p>
                  </div>
                  <div className="relative p-4">
                    <div className="space-y-2 filter blur-[6px] select-none" aria-hidden="true">
                      <div className="h-3.5 bg-slate-200 rounded w-11/12"></div>
                      <div className="h-3.5 bg-slate-200 rounded w-10/12"></div>
                      <div className="h-3.5 bg-slate-200 rounded w-9/12"></div>
                      <div className="h-3.5 bg-slate-200 rounded w-11/12"></div>
                      <div className="mt-3 h-3.5 bg-slate-200 rounded w-6/12"></div>
                      <div className="h-3.5 bg-slate-200 rounded w-8/12"></div>
                    </div>
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-b from-white/60 to-white/90">
                      <Lock size={16} className="text-slate-500 mb-1" />
                      <p className="text-[12px] text-slate-700 text-center max-w-[340px] leading-snug">
                        Full About rewrite, experience recommendations and keyword suggestions unlock with any plan.
                      </p>
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setStep(3)}
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-yellow-400 hover:bg-yellow-300 text-slate-900 text-[14.5px] font-bold h-11 transition-colors"
                  data-testid="hero4-cta-step3"
                >
                  Unlock My Complete Profile Rewrite <ArrowRight size={16} />
                </button>
                <p className="mt-2 text-[11px] text-slate-500 text-center">
                  Get your new headline, About section, experience recommendations and profile keywords.
                </p>
              </div>
            )}

            {step === 3 && (
              <div data-testid="hero4-step4">
                <p className="text-[11px] font-mono-edge tracking-[0.2em] uppercase text-sky-600">Step 4 of 4 · Select your makeover</p>
                <h2 className="mt-1 text-[22px] font-bold text-slate-900 leading-tight">Choose how you want your profile rewritten.</h2>
                <p className="mt-1.5 text-[13px] text-slate-600 leading-relaxed">
                  Both options include a complete, goal-focused LinkedIn profile makeover.
                </p>

                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="hero4-plans">
                  {[
                    {
                      key: 'one_time',
                      badge: 'FASTEST',
                      title: 'AI Profile Makeover',
                      price: currency === 'INR' ? '₹199' : '$9',
                      features: [
                        'Ready in approximately 5 minutes',
                        'Complete profile audit',
                        'Headline and About rewrite',
                        'Experience recommendations',
                        'Keyword suggestions',
                        'Goal-based positioning',
                      ],
                      cta: 'Get Makeover Now',
                    },
                    {
                      key: 'expert_audit',
                      badge: 'EXPERT REVIEW',
                      title: 'Expert-Audited Makeover',
                      price: currency === 'INR' ? '₹399' : '$15',
                      features: [
                        'Ready within 4 hours',
                        'Reviewed and refined by an expert',
                        'Headline and About rewrite',
                        'Experience recommendations',
                        'Two profile versions',
                        'Storytelling and balanced versions',
                      ],
                      cta: 'Get Makeover Now',
                    },
                  ].map((p) => {
                    const active = payPlan === p.key;
                    return (
                      <div
                        key={p.key}
                        className={`rounded-2xl border-2 p-4 flex flex-col ${
                          active ? 'border-sky-500 bg-sky-50/60 shadow-[0_10px_30px_-15px_rgba(56,189,248,0.5)]' : 'border-slate-200 bg-white'
                        }`}
                        data-testid={`hero4-plan-${p.key}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className={`inline-flex items-center gap-1 rounded-full text-[10px] font-mono-edge tracking-[0.14em] uppercase px-2 py-0.5 ${p.key === 'one_time' ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-700 border border-slate-200'}`}>
                            {p.key === 'one_time' ? <Zap size={10} /> : <ShieldCheck size={10} />}
                            {p.badge}
                          </span>
                          {active && <CheckCircle2 size={16} className="text-sky-500" />}
                        </div>
                        <p className="mt-2 text-[15px] font-bold text-slate-900 leading-tight">{p.title}</p>
                        <p className="mt-1 text-[24px] font-extrabold text-sky-600 tabular-nums leading-none">{p.price}</p>
                        <ul className="mt-3 space-y-1 flex-1">
                          {p.features.map((f) => (
                            <li key={f} className="flex items-start gap-1.5 text-[11.5px] text-slate-700 leading-snug">
                              <CheckCircle2 size={11} className="mt-0.5 shrink-0 text-emerald-500" /> {f}
                            </li>
                          ))}
                        </ul>
                        <button
                          type="button"
                          onClick={() => chooseMakeover(p.key)}
                          className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-yellow-400 hover:bg-yellow-300 text-slate-900 text-[12.5px] font-bold h-10 transition-colors"
                          data-testid={`hero4-cta-plan-${p.key}`}
                        >
                          {p.cta}
                        </button>
                      </div>
                    );
                  })}
                </div>

                {/* Pay panel — appears the moment a plan is chosen.
                    Non-INR-onetime keeps the Razorpay hosted button widget
                    permanently mounted here so payment-button.js only
                    initialises once (repeated mounts trigger Razorpay's
                    429 "Too many requests"). INR one_time is intentionally
                    routed through `GuestCheckoutDialog` instead → the
                    pay panel is not shown for that combo. */}
                {payPlan && !(payPlan === 'one_time' && currency === 'INR') && (
                  <div
                    className="mt-4 rounded-2xl border border-sky-200 bg-sky-50/60 p-3.5"
                    data-testid="hero4-pay-panel"
                  >
                    {(() => {
                      const bid = BUTTON_IDS[payPlan]?.[currency];
                      if (!bid) return (
                        <p className="text-[12px] text-slate-600 text-center">Preparing checkout…</p>
                      );
                      return (
                        <div className="flex justify-center" data-testid="hero4-razorpay-wrap">
                          <RazorpayPaymentButton
                            buttonId={bid}
                            testId={`hero4-razorpay-${payPlan}-${currency}`}
                          />
                        </div>
                      );
                    })()}
                    <p className="mt-2 text-[10.5px] text-slate-500 text-center">
                      Secure payment · No LinkedIn password required · Downloadable PDF
                    </p>
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="mt-4 inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white text-slate-600 text-[12.5px] font-semibold h-9 px-3"
                  data-testid="hero4-back-step4"
                >
                  <ArrowLeft size={13} /> Back to my audit
                </button>
              </div>
            )}
          </motion.div>
        </div>
      </div>

      <GuestCheckoutDialog
        open={guestCheckoutOpen}
        onOpenChange={setGuestCheckoutOpen}
        planLabel="AI Profile Makeover"
        priceLabel={currency === 'INR' ? '₹199' : '$9'}
        triggerTestId="hero4-guest-checkout-dialog"
      />
    </section>
  );
}

export default HomeHero4Step;
